import type { SkillMetadata, SkillMetadataMap } from '../types';

/**
 * Structural shape of an action-like object that the util can extract a skill
 * slug from. Callers may pass any object matching this shape — a full
 * `ConversationAction` from `@atlassian/rovo-conversation-actions`, a
 * lightweight `SpaceStarter`, or a plain object literal — without
 * `@atlassian/rovo-skills` needing to depend on the action type itself.
 */
export type SkillActionLike = {
	handleType?: string;
	staticPrompt?: string;
};

/**
 * Returns the skill metadata for a skill-typed action by extracting the skill
 * slug from its `staticPrompt` (first slash-prefixed token) and looking it up
 * in the metadata map.
 *
 * Example: `"/create-plan-release Create a release"` → `"create-plan-release"`
 * → `skillMetadataMap["create-plan-release"]`.
 *
 * Returns `undefined` when the action isn't skill-typed
 * (`handleType !== 'Skill'`), has no `staticPrompt`, the token isn't a slash
 * command, or no matching entry exists in the map.
 *
 * @remarks
 * The literal `'Skill'` compared against `action.handleType` mirrors
 * `HandleActionType.Skill` from `@atlassian/rovo-conversation-actions`.
 * `HandleActionType` is a string enum whose members equal their names, so the
 * literal comparison is stable. It is duplicated as a raw string here rather
 * than imported to avoid taking a dependency on `rovo-conversation-actions`
 * from `rovo-skills`; keep the two in sync if `HandleActionType.Skill` ever
 * changes value.
 * @see HandleActionType.Skill in `@atlassian/rovo-conversation-actions`.
 */
export function getSkillMetadataForAction(
	action: SkillActionLike | undefined,
	skillMetadataMap: SkillMetadataMap | undefined,
): SkillMetadata | undefined {
	if (!action || !skillMetadataMap || action.handleType !== 'Skill' || !action.staticPrompt) {
		return undefined;
	}

	const firstToken = action.staticPrompt.trim().split(/\s+/, 1)[0];
	if (!firstToken || !firstToken.startsWith('/')) {
		return undefined;
	}

	return skillMetadataMap[firstToken.slice(1)];
}
